import { useContext } from 'react';
import {
  Form,
  FieldSet,
  Button,
  ColumnLayout,
  Column,
  Typography,
  ModalBody,
} from '@visionplanner/ui-react-material';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import { otherSoftwaresUsedFormSchema } from './partner-form-validation';
import { saveOtherSoftwares } from '../partner-onboarding.requests';
import { StyledModalFooter } from '../../styles';
import { PartnerContext } from '../partner-context';
import { types } from '../partner-onboarding.reducer';
import { useAsyncCallback } from '../../../common/custom-hooks/use-async-callback';
import { STEP } from '../partner-onboarding.steps';

/** Component for updating default softwares
 *  used by partner
 */
const OtherSoftwaresUsed = ({
  administrationSoftwares, payrollSoftwares, taxationSoftwares,
}) => {
  const {
    activeStep,
    setActiveStep,
    state,
    dispatch,
  } = useContext(PartnerContext);
  const { partnerId, otherSoftwaresUsed } = state;

  const handleSubmit = useAsyncCallback(async (formValues) => {
    if (!isEqual(otherSoftwaresUsed, formValues)) {
      await saveOtherSoftwares(partnerId, formValues);
      dispatch({
        type: types.saveOtherSoftawaresUsed,
        payload: formValues,
      });
    }
    setActiveStep(STEP.invoiceInformation);
  }, [dispatch, otherSoftwaresUsed, partnerId, setActiveStep]);

  return (
    <Form
      initialValues={otherSoftwaresUsed}
      validateFormSchema={otherSoftwaresUsedFormSchema}
      onSubmit={handleSubmit}
      dataTa="other-softwares-used-form"
      isInitiallyValid={otherSoftwaresUsedFormSchema.isValidSync(otherSoftwaresUsed)}
      className="full-height flex-column"
    >
      {
        ({
          isValid, isSubmitting,
        }) => (
          <>
            <ModalBody>
              <Typography use="h6">Overige software</Typography>
              <ColumnLayout>
                <Column span={5}>
                  <FieldSet
                    type="selectOne"
                    controlType="autocomplete"
                    label="Standaard administratieprogramma"
                    name="administrationSoftwareId"
                    options={administrationSoftwares}
                    orientation="horizontal"
                    placeholder="Selecteer software"
                    required
                    dataTa="administration-list"
                    noOptionsMessage="Geen opties"
                    className="administration-list"
                  />
                </Column>
              </ColumnLayout>
              <ColumnLayout>
                <Column span={5}>
                  <FieldSet
                    type="selectOne"
                    controlType="autocomplete"
                    label="Standaard loonadministratieprogramma"
                    name="payrollSoftwareId"
                    options={payrollSoftwares}
                    orientation="horizontal"
                    placeholder="Selecteer software"
                    required
                    dataTa="payroll-list"
                    noOptionsMessage="Geen opties"
                    className="payroll-list"
                  />
                </Column>
              </ColumnLayout>
              <ColumnLayout>
                <Column span={5}>
                  <FieldSet
                    type="selectOne"
                    controlType="autocomplete"
                    label="Standaard aangiftesoftware"
                    name="taxationSoftwareId"
                    options={taxationSoftwares}
                    orientation="horizontal"
                    placeholder="Selecteer software"
                    required
                    dataTa="taxation-list"
                    noOptionsMessage="Geen opties"
                    className="taxation-list"
                  />
                </Column>
              </ColumnLayout>
            </ModalBody>
            <StyledModalFooter>
              <Button buttonType="secondary" dataTa="go-to-add-employee" onClick={() => setActiveStep(activeStep - 1)}>Vorige</Button>
              <Button type="submit" dataTa="go-to-invoice-information" disabled={!isValid || isSubmitting}>Volgende</Button>
            </StyledModalFooter>
          </>
        )
      }
    </Form>
  );
};

OtherSoftwaresUsed.propTypes = {
  /** List of administrationSoftwares */
  administrationSoftwares: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  /** List of payrollSoftwares */
  payrollSoftwares: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  /** List of taxationSoftwares */
  taxationSoftwares: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

export default OtherSoftwaresUsed;
